import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { GraduationCap, BookMarked, ListOrdered } from "lucide-react";
import { api } from "@/components/Common";
import { Page, PageHeader, Loading, ErrorNote, Medallion, SectionCard } from "@/components/Common";

export function UgcPage() {
  const [units, setUnits] = useState(null);
  useEffect(() => {
    api.get("/units").then((r) => setUnits(r.data)).catch(() => setUnits([]));
  }, []);

  return (
    <Page>
      <PageHeader title="UGC NET اردو" subtitle="Paper 1 (General) اور Paper 2 (Urdu) کی مکمل منظم تیاری" crumbs={[{ label: "UGC NET اردو" }]} />

      <div className="bg-[#e8dfcc] border border-[#c5a059]/60 rounded-2xl p-6 sm:p-8 mb-10" data-testid="paper1-card">
        <div className="flex items-center gap-3">
          <GraduationCap size={26} className="text-[#2a1508]" />
          <h2 className="text-2xl font-bold text-[#2a1508] leading-[2]">Paper 1 — General</h2>
        </div>
        <p className="mt-2 text-lg text-[#4a2b16] leading-[2.2]">تدریسی و تحقیقی استعداد کا عمومی پرچہ — یہ حصہ جلد شامل کیا جائے گا۔</p>
      </div>

      <h2 className="text-2xl sm:text-3xl font-bold text-[#2a1508] leading-[2] mb-6 flex items-center gap-2">
        <BookMarked size={24} className="text-[#c5a059]" /> Paper 2 — Urdu
      </h2>
      {!units ? (
        <Loading />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="units-grid">
          {units.map((u) => (
            <Link
              key={u.num}
              to={`/ugc-net/unit/${u.num}`}
              data-testid={`unit-card-${u.num}`}
              className="group bg-[#fdfbf7] border border-[#d4af37]/40 rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_30px_rgba(42,21,8,0.12)] hover:border-[#d4af37]"
            >
              <span className="font-latin text-sm tracking-widest text-[#c5a059]">UNIT {u.num}</span>
              <h3 className="mt-1 text-xl font-bold text-[#2a1508] leading-[2]">{u.title}</h3>
              <p className="mt-2 text-base text-[#5c3a21] leading-[2.2] line-clamp-2">{u.desc}</p>
            </Link>
          ))}
        </div>
      )}
    </Page>
  );
}

export function UnitPage() {
  const { num } = useParams();
  const [unit, setUnit] = useState(null);
  const [authors, setAuthors] = useState([]);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setUnit(null);
    setErr(false);
    api.get(`/units/${num}`).then((r) => setUnit(r.data)).catch(() => setErr(true));
    api.get("/authors").then((r) => setAuthors(r.data)).catch(() => {});
  }, [num]);

  if (err) return <Page><ErrorNote message="یونٹ نہیں ملی" /></Page>;
  if (!unit) return <Page><Loading /></Page>;

  const authorCards = unit.cards?.filter((c) => c.kind === "authors") || [];
  const linkCards = unit.cards?.filter((c) => c.kind !== "authors") || [];

  return (
    <Page>
      <PageHeader
        title={unit.title}
        subtitle={unit.desc}
        crumbs={[{ label: "UGC NET اردو", to: "/ugc-net" }, { label: `یونٹ ${unit.num}` }]}
      />

      {unit.topics?.length > 0 && (
        <div className="bg-[#fdfbf7] border border-[#d4af37]/40 rounded-2xl p-6 sm:p-8 mb-10" data-testid="unit-topics">
          <h3 className="text-2xl font-bold text-[#2a1508] leading-[2] mb-4 flex items-center gap-2">
            <ListOrdered size={22} className="text-[#c5a059]" /> موضوعات
          </h3>
          <ol className="space-y-1">
            {unit.topics.map((t, i) => {
              const title = typeof t === "string" ? t : t.title;
              const slug = typeof t === "object" ? t.slug : null;
              return (
                <li key={i} className="flex items-baseline gap-3 text-lg text-[#33200f] leading-[2.2]">
                  <span className="font-ui text-sm text-[#c5a059] shrink-0">{i + 1}.</span>
                  {slug ? (
                    <Link to={`/pages/${slug}`} data-testid={`topic-link-${slug}`} className="hover:text-[#2a1508] underline decoration-[#c5a059]/60 underline-offset-8 decoration-dotted transition-colors">
                      {title}
                    </Link>
                  ) : (
                    title
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {linkCards.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10" data-testid="unit-cards">
          {linkCards.map((c, i) =>
            c.kind === "note" ? (
              <div key={i} data-testid={`unit-card-note-${i}`} className="bg-[#f5efe3] border border-dashed border-[#c5a059]/70 rounded-xl p-6 text-center">
                <h3 className="text-xl font-semibold text-[#5c3a21] leading-[2]">{c.title}</h3>
                {c.desc && <p className="text-sm text-[#5c3a21] mt-2 leading-[2]">{c.desc}</p>}
              </div>
            ) : (
              <SectionCard key={i} to={c.kind === "genre" ? `/genres/${c.ref}` : `/pages/${c.ref}`} title={c.title} desc={c.desc} testid={`unit-card-link-${c.ref || i}`} />
            )
          )}
        </div>
      )}

      {authorCards.map((c, ci) => (
        <div key={ci} className="mb-10" data-testid={`unit-authors-${ci}`}>
          <h3 className="text-2xl font-bold text-[#2a1508] leading-[2] mb-2">{c.title}</h3>
          <p className="text-base text-[#5c3a21] leading-[2] mb-5">{c.desc}</p>
          {c.slugs?.length ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {c.slugs.map((s) => {
                const a = authors.find((x) => x.slug === s);
                if (!a) return null;
                return (
                  <Link key={s} to={`/authors/${s}`} data-testid={`unit-author-${s}`} className="group bg-[#fdfbf7] border border-[#d4af37]/40 rounded-xl p-5 text-center transition-all duration-300 hover:-translate-y-1 hover:border-[#d4af37]">
                    <Medallion name={a.name} className="w-16 h-16 mx-auto text-3xl" />
                    <h4 className="mt-3 text-lg font-semibold text-[#2a1508] leading-[2]">{a.name}</h4>
                    <p className="font-ui text-sm text-[#5c3a21]">{a.years}</p>
                  </Link>
                );
              })}
            </div>
          ) : (
            <p className="text-lg text-[#5c3a21] bg-[#fdfbf7] border border-dashed border-[#c5a059]/60 rounded-xl p-5">فہرست زیر تکمیل — ایڈمن پینل سے شامل کی جائے گی</p>
          )}
        </div>
      ))}

      {unit.works?.length > 0 && (
        <div data-testid="unit-works">
          <h3 className="text-2xl font-bold text-[#2a1508] leading-[2] mb-4">متون و تفصیلی مطالعہ</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {unit.works.map((w) => (
              <Link key={w.slug} to={`/works/${w.slug}`} data-testid={`unit-work-${w.slug}`} className="bg-[#fdfbf7] border border-[#d4af37]/40 rounded-xl px-5 py-4 text-lg text-[#2a1508] leading-[2] hover:border-[#d4af37] hover:-translate-y-0.5 transition-all duration-300">
                {w.title}
              </Link>
            ))}
          </div>
        </div>
      )}
    </Page>
  );
}
